import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import CouponInput from '../AdminDashboard/CouponInput';
import MockTestStartPage from './MockTestStartPage';
import "./mock.css"
import MockTestInstructionsPage from './MockTestInstructionsPage';
import {
  FaCalculator,
  FaBrain,
  FaPenFancy,
  FaBook,
  FaChevronLeft,
  FaHistory,
  FaChevronRight,
  FaLock,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaPlay
} from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const subjectConfig = {
  Maths: {
    icon: <FaCalculator className="text-xl" />,
    color: "from-blue-500 to-teal-400",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    route: "/mock-test/maths"
  },
  ThinkingSkills: {
    icon: <FaBrain className="text-xl" />,
    color: "from-purple-500 to-pink-400",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    route: "/mock-test/thinkingskills"
  },
  Writing: {
    icon: <FaPenFancy className="text-xl" />,
    color: "from-orange-500 to-yellow-400",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
    route: "/mock-test/writing"
  },
  Reading: {
    icon: <FaBook className="text-xl" />,
    color: "from-teal-500 to-green-400",
    bgColor: "bg-teal-50",
    textColor: "text-teal-700",
    route: "/mock-test/reading"
  }
};

const MockTestsPage = () => {
  const navigate = useNavigate();
  const [subjectsStatus, setSubjectsStatus] = useState([]);
  const [testHistory, setTestHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState(null);
  const [appliedCoupons, setAppliedCoupons] = useState({});
  const [expandedTests, setExpandedTests] = useState({});
  const [hasMockTestAccess, setHasMockTestAccess] = useState(false);
  const [mockTests, setMockTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showTestStartPage, setShowTestStartPage] = useState(false);
  const [showInstructionsPage, setShowInstructionsPage] = useState(false);
  const [testProgress, setTestProgress] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setStudentId(user.id);

        try {
          // Fetch mock test package access status
          const mockTestAccessResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/mocktest-package-access/${user.id}`
          );

          if (mockTestAccessResponse.ok) {
            const accessData = await mockTestAccessResponse.json();
            setHasMockTestAccess(accessData.hasAccess);

            if (accessData.hasAccess) {
              // Fetch available mock tests
              const mockTestsResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/package/tests`
              );
              if (mockTestsResponse.ok) {
                const testsData = await mockTestsResponse.json();
                setMockTests(testsData.tests || []);
              }
            }
          }

          // Fetch test history to determine progress
          const historyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
          );
          if (historyResponse.ok) {
            const historyData = await historyResponse.json();
            const mockTestHistory = historyData.filter(test =>
              test.testType && test.testType.toLowerCase() === 'mock'
            );
            setTestHistory(mockTestHistory);

            // Calculate test progress
            const progress = calculateTestProgress(mockTestHistory);
            setTestProgress(progress);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, []);

  // Enhanced test progress calculation - ONLY for package tests
  const calculateTestProgress = (history) => {
    const progress = {};
    const allSubjects = ['Maths', 'ThinkingSkills', 'Writing', 'Reading'];

    // Initialize progress for all 10 tests
    for (let i = 1; i <= 10; i++) {
      progress[i] = {
        completedSubjects: new Set(),
        isCompleted: false,
        testName: `Mock Test ${i}`
      };
    }

    // Group tests by mock test number and subject - ONLY include package tests
    history.forEach(test => {
      // Skip if not a mock test
      if (test.testType !== 'mock') return;

      // ⚡ IMPORTANT: Skip individual tests - they don't count towards package progress
      if (test.test_category === 'individual-mock') {
        console.log('Skipping individual test:', test.testId);
        return;
      }

      // Extract test number from package_test_id or testId - ADD SAFETY CHECKS
      let testNumber = 1;

      if (test.package_test_id && typeof test.package_test_id === 'string') {
        const packageMatch = test.package_test_id.match(/mock-package-(\d+)/);
        if (packageMatch) {
          testNumber = parseInt(packageMatch[1]);
        }
      } else if (test.testId && typeof test.testId === 'string') {
        const testMatch = test.testId.match(/mock-package-(\d+)/);
        if (testMatch) {
          testNumber = parseInt(testMatch[1]);
        }
      } else if (test.testName && typeof test.testName === 'string') {
        const nameMatch = test.testName.match(/Mock Test (\d+)/);
        if (nameMatch) {
          testNumber = parseInt(nameMatch[1]);
        }
      }

      // Map subject names to match configuration
      let subject = test.subject;
      if (subject === 'maths') subject = 'Maths';
      if (subject === 'thinkingskills') subject = 'ThinkingSkills';
      if (subject === 'writing') subject = 'Writing';
      if (subject === 'reading') subject = 'Reading';

      if (allSubjects.includes(subject) && testNumber <= 10) {
        progress[testNumber].completedSubjects.add(subject);

        // Check if all subjects are completed for this test
        if (progress[testNumber].completedSubjects.size === allSubjects.length) {
          progress[testNumber].isCompleted = true;
        }
      }
    });

    return progress;
  };

  // Check if a test is available (first test is always available, others require previous completion)
  const isTestAvailable = (testIndex) => {
    if (testIndex === 0) return true; // First test is always available

    // Check if previous test is completed
    const previousTestNumber = testIndex; // Since testIndex is 0-based, previous test is testIndex
    return testProgress[previousTestNumber]?.isCompleted || false;
  };

  // Check if a subject in a test is completed
  const isSubjectCompleted = (testIndex, subject) => {
    const testNumber = testIndex + 1;

    // Check test history for completed package tests only
    return testHistory.some(test => {
      // Must be a mock test
      if (test.testType !== 'mock') return false;

      // ⚡ Skip individual tests
      if (test.test_category === 'individual-mock') return false;

      // Get test number - ADD SAFETY CHECKS
      let testNumberFromHistory = 1;

      // Check package_test_id first
      if (test.package_test_id && typeof test.package_test_id === 'string') {
        const match = test.package_test_id.match(/mock-package-(\d+)/);
        if (match) testNumberFromHistory = parseInt(match[1]);
      }
      // Fallback to testId - ADD SAFETY CHECK
      else if (test.testId && typeof test.testId === 'string') {
        const match = test.testId.match(/mock-package-(\d+)/);
        if (match) testNumberFromHistory = parseInt(match[1]);
      }

      // Map subject
      let testSubject = test.subject;
      if (testSubject === 'maths') testSubject = 'Maths';
      if (testSubject === 'thinkingskills') testSubject = 'ThinkingSkills';
      if (testSubject === 'writing') testSubject = 'Writing';
      if (testSubject === 'reading') testSubject = 'Reading';

      return testNumberFromHistory === testNumber && testSubject === subject;
    });
  };

  const toggleTestExpansion = (testId) => {
    setExpandedTests(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  const handleStartTestFlow = (test) => {
    setSelectedTest(test);
    setShowTestStartPage(true);
  };

  const handleBackFromStartPage = () => {
    setShowTestStartPage(false);
    setSelectedTest(null);
  };

  const handleBackFromInstructions = () => {
    setShowInstructionsPage(false);
    setSelectedSubject(null);
  };

  const handleStartSubjectTest = (testId, subject, showInstructions = false) => {
  if (!hasMockTestAccess) {
    alert("Please purchase the mock test package to access this test");
    return;
  }

  if (showInstructions) {
    setSelectedSubject(subject);
    setShowInstructionsPage(true);
  } else {
    const config = subjectConfig[subject];
    if (config) {
      // Extract test number from testId (e.g., "mock-package-5" -> 5)
      const testNumber = testId.split('-')[2];
      
      navigate(config.route, { 
        state: { 
          testId, 
          packageTest: true, 
          testNumber: parseInt(testNumber),
          testName: `Mock Test ${testNumber} ${subject}` 
        } 
      });
    }
  }
};

  const handleStartTestFromInstructions = () => {
  if (selectedTest && selectedSubject) {
    const config = subjectConfig[selectedSubject];
    if (config) {
      const testNumber = selectedTest.id.split('-')[2];
      navigate(config.route, {
        state: {
          testId: selectedTest.id,
          packageTest: true,
          testNumber: parseInt(testNumber),
          testName: `Mock Test ${testNumber} ${selectedSubject}`
        }
      });
    }
  }
};

  const handleBuyNow = async (e) => {
    e.preventDefault();
    const stripe = await stripePromise;
    const userData = localStorage.getItem("user");
    const user = userData ? JSON.parse(userData) : null;

    const endpoint = appliedCoupons['mocktest-package']
      ? "/api/payment/create-mocktest-package-session-with-coupon"
      : "/api/payment/create-mocktest-package-session";

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        student_id: studentId,
        coupon_code: appliedCoupons['mocktest-package']?.id
      }),
    });

    if (response.ok) {
      const session = await response.json();
      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) alert(result.error.message);
    } else {
      alert("Error creating payment session");
    }
  };

  const formatTestDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Function to get display name for subjects (same as in MockTestStartPage)
  const getSubjectDisplayName = (subjectKey) => {
    // Normalize the subject key first
    let normalizedKey = subjectKey;
    if (typeof normalizedKey === 'string') {
      normalizedKey = normalizedKey.trim();
      if (normalizedKey === 'maths') normalizedKey = 'Maths';
      if (normalizedKey === 'thinkingskills') normalizedKey = 'ThinkingSkills';
      if (normalizedKey === 'writing') normalizedKey = 'Writing';
      if (normalizedKey === 'reading') normalizedKey = 'Reading';
    }

    const displayNames = {
      'Maths': 'Mathematical Reasoning Mock Test',
      'ThinkingSkills': 'Thinking Skills Mock Test',
      'Reading': 'Reading Mock Test',
      'Writing': 'Writing Mock Test'
    };
    return displayNames[normalizedKey] || subjectKey;
  };

  // Fixed test display name function
  const getTestDisplayName = (test) => {
    if (test.testType === 'mock') {
      // If testName exists, use it
      if (test.testName) {
        return test.testName;
      }

      // For package tests with testId - ADD SAFETY CHECK
      if (test.testId && typeof test.testId === 'string') {
        const testMatch = test.testId.match(/mock-package-(\d+)/);
        if (testMatch) {
          const testNumber = testMatch[1];
          const subjectDisplayName = getSubjectDisplayName(test.subject || '');
          return `Mock Test ${testNumber} - ${subjectDisplayName}`;
        }
      }

      // For regular mock tests - just return the subject display name
      if (test.subject) {
        return getSubjectDisplayName(test.subject);
      }

      return 'Mock Test';
    }

    // For non-mock tests
    return `${getSubjectDisplayName(test.subject || test.category || 'Unknown')} ${test.testType || 'Test'}`;
  };


  // ✅ EXACT SAME SCORE DISPLAY LOGIC AS OLD CODE
  const getScoreDisplay = (test) => {
    // For writing tests, show the actual score (0-10) instead of percentage
    if (test.subject === 'writing') {
      // ✅ FIXED: Use teacher_score if available, otherwise use score
      // Check if teacher_score exists and is not null/undefined
      const hasTeacherScore = test.teacher_score !== null && test.teacher_score !== undefined;
      const writingScore = hasTeacherScore ? test.teacher_score : (test.score || 0);

      return {
        displayValue: `${writingScore}/10`,
        percentage: writingScore * 10, // Convert 0-10 to 0-100 for progress bar
        isWriting: true,
        rawScore: writingScore,
        hasTeacherScore: hasTeacherScore // ✅ ADD: Flag to indicate if teacher scored it
      };
    } else {
      // For MCQ subjects, use percentage
      return {
        displayValue: `${Math.round(test.scorePercentage || 0)}%`,
        percentage: test.scorePercentage || 0,
        isWriting: false,
        rawScore: test.correctAnswers || 0
      };
    }
  };

  // ✅ EXACT SAME SCORE TEXT LOGIC AS OLD CODE
  const getScoreText = (test) => {
    if (test.subject === 'writing') {
      const scoreData = getScoreDisplay(test);

      if (scoreData.rawScore >= 8) return 'Excellent';
      if (scoreData.rawScore >= 6) return 'Good';
      if (scoreData.rawScore >= 4) return 'Average';
      return 'Needs Improvement';
    } else {
      const percentage = test.scorePercentage || 0;
      if (percentage >= 70) return 'Excellent';
      if (percentage >= 40) return 'Good';
      return 'Needs Improvement';
    }
  };

  // Show instructions page if selected
  if (showInstructionsPage && selectedTest && selectedSubject) {
    return (
      <MockTestInstructionsPage
        test={selectedTest}
        subject={selectedSubject}
        onBack={handleBackFromInstructions}
        onStartTest={handleStartTestFromInstructions} // No parameters needed here
      />

    );
  }

  // Show test start page if selected
  if (showTestStartPage && selectedTest) {
    return (
      <MockTestStartPage
        test={selectedTest}
        onBack={handleBackFromStartPage}
        onStartTest={handleStartSubjectTest}
        testProgress={testProgress} // Add this line
      />
    );
  }

  const renderMockTestPackage = () => {
    if (hasMockTestAccess) {
      return (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">
                Your Mock Test Package
              </h3>
              <p className="text-gray-600 mt-1">10 Complete Mock Tests with all 4 subjects</p>
            </div>
            <div className="bg-green-100 text-green-800 px-3 py-2 rounded-full text-sm font-medium">
              Package Active
            </div>
          </div>

          <div className="space-y-4">
            {mockTests.map((test, index) => {
              const isAvailable = isTestAvailable(index);
              const isCompleted = testProgress[index + 1]?.isCompleted || false;
              const completedSubjects = testProgress[index + 1]?.completedSubjects?.size || 0;
              const testNumber = index + 1;

              return (
                <div key={test.id} className={`border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow ${!isAvailable ? 'opacity-50' : ''
                  }`}>
                  <div
                    className={`p-4 flex justify-between items-center cursor-pointer transition-colors ${isAvailable ? 'bg-gray-50 hover:bg-gray-100' : 'bg-gray-100'
                      }`}
                    onClick={() => isAvailable && toggleTestExpansion(test.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={isCompleted ? 'test-number-completed' : isAvailable ? 'test-number-available' : 'test-number-locked'}>
                        {isCompleted ? '✓' : testNumber}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800 text-lg">Mock Test {testNumber}</h4>
                        <p className="text-sm text-gray-600">
                          {isCompleted
                            ? 'Completed - All subjects finished'
                            : isAvailable
                              ? `${completedSubjects}/4 subjects completed`
                              : 'Complete previous test to unlock'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!isAvailable && (
                        <FaLock className="text-gray-400" />
                      )}
                      <span className="text-sm text-gray-500">
                        {expandedTests[test.id] ? 'Hide' : isAvailable ? 'Show' : 'Locked'}
                      </span>
                      {isAvailable && (
                        expandedTests[test.id] ?
                          <FaChevronUp className="text-gray-400" /> :
                          <FaChevronDown className="text-gray-400" />
                      )}
                    </div>
                  </div>

                  {expandedTests[test.id] && isAvailable && (
                    <div className="p-6 bg-white border-t border-gray-200">
                      <div className="text-center mb-6">
                        <p className="text-gray-600 mb-4">
                          This mock test contains all 4 subjects. Click the button below to begin.
                        </p>

                        <button
                          onClick={() => handleStartTestFlow(test)}
                          className="start-test-button"
                        >
                          Start Mock Test {testNumber}
                        </button>
                      </div>

                      {/* Subject Progress with Scores */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        {Object.entries(subjectConfig).map(([subject, config]) => {
                          const isSubjectDone = isSubjectCompleted(index, subject);
                          const subjectStatus = isSubjectDone ? 'Completed' : 'Not Started';

                          // Find the test history for this specific subject and test - SIMPLIFIED
                          // Find the test history for this specific subject and test - SIMPLIFIED
                          const subjectTestHistory = testHistory.find(testItem => {
                            if (!testItem || testItem.testType !== 'mock') return false;

                            // ⚡ Skip individual tests
                            if (testItem.test_category === 'individual-mock') return false;

                            // Map subject names to match our configuration
                            let testSubject = testItem.subject;
                            if (testSubject === 'maths') testSubject = 'Maths';
                            if (testSubject === 'thinkingskills') testSubject = 'ThinkingSkills';
                            if (testSubject === 'writing') testSubject = 'Writing';
                            if (testSubject === 'reading') testSubject = 'Reading';

                            // Check if subject matches
                            const subjectMatch = testSubject === subject;

                            // Check if it's from the same mock test number - ADD SAFETY CHECKS
                            let testNumberFromHistory = 1;

                            // Check package_test_id first
                            if (testItem.package_test_id && typeof testItem.package_test_id === 'string') {
                              const testMatch = testItem.package_test_id.match(/mock-package-(\d+)/);
                              if (testMatch) {
                                testNumberFromHistory = parseInt(testMatch[1]);
                              }
                            }
                            // Fallback to testId - ADD SAFETY CHECK
                            else if (testItem.testId && typeof testItem.testId === 'string') {
                              const testMatch = testItem.testId.match(/mock-package-(\d+)/);
                              if (testMatch) {
                                testNumberFromHistory = parseInt(testMatch[1]);
                              }
                            }

                            return subjectMatch && testNumberFromHistory === testNumber;
                          });

                          // Get score display for completed subjects
                          const scoreData = subjectTestHistory ? getScoreDisplay(subjectTestHistory) : null;
                          const scoreText = subjectTestHistory ? getScoreText(subjectTestHistory) : null;

                          // Get the correct CSS class based on subject
                          const getSubjectIconClass = (subject) => {
                            switch (subject) {
                              case 'Maths': return 'subject-icon-maths';
                              case 'ThinkingSkills': return 'subject-icon-thinkingskills';
                              case 'Writing': return 'subject-icon-writing';
                              case 'Reading': return 'subject-icon-reading';
                              default: return 'subject-icon-maths';
                            }
                          };

                          // ✅ UPDATED: Use display names without "Mock Test" for expand section
                          const getSubjectDisplayNameForExpand = (subjectKey) => {
                            let normalizedKey = subjectKey;
                            if (typeof normalizedKey === 'string') {
                              normalizedKey = normalizedKey.trim();
                              if (normalizedKey === 'maths') normalizedKey = 'Maths';
                              if (normalizedKey === 'thinkingskills') normalizedKey = 'ThinkingSkills';
                              if (normalizedKey === 'writing') normalizedKey = 'Writing';
                              if (normalizedKey === 'reading') normalizedKey = 'Reading';
                            }

                            const displayNames = {
                              'Maths': 'Mathematical Reasoning',
                              'ThinkingSkills': 'Thinking Skills',
                              'Reading': 'Reading',
                              'Writing': 'Writing'
                            };
                            return displayNames[normalizedKey] || subjectKey;
                          };

                          const subjectDisplayName = getSubjectDisplayNameForExpand(subject);

                          return (
                            <div key={subject} className={`subject-progress-card ${isSubjectDone ? 'subject-completed' : ''}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className={getSubjectIconClass(subject)}>
                                    {config.icon}
                                  </div>
                                  <div className="flex-1">
                                    {/* ✅ UPDATED: Use display names without "Mock Test" */}
                                    <h5 className="font-semibold text-gray-800">{subjectDisplayName}</h5>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Status: <span className={isSubjectDone ? 'subject-status-completed' : 'subject-status-not-started'}>
                                        {subjectStatus}
                                      </span>
                                    </p>
                                    {/* Show score for completed subjects */}
                                    {isSubjectDone && scoreData && (
                                      <div className="flex items-center space-x-2 mt-1">
                                        <span className="text-sm font-medium text-gray-700">
                                          Score: {scoreData.displayValue}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs ${scoreData.percentage >= 70 ? 'bg-green-600 text-white' :
                                          scoreData.percentage >= 40 ? 'bg-blue-100 text-blue-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                          {scoreText}
                                        </span>
                                      </div>
                                    )}
                                    {/* Show message if completed but no score data found */}
                                    {isSubjectDone && !scoreData && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        Score data loading...
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {isSubjectDone && scoreData && (
                                  <div style={{ width: 40, height: 40 }}>
                                    <CircularProgressbar
                                      value={scoreData.percentage}
                                      text={scoreData.isWriting ? `${scoreData.rawScore}` : `${Math.round(scoreData.percentage)}%`}
                                      styles={buildStyles({
                                        textSize: scoreData.isWriting ? '28px' : '28px',
                                        pathColor: scoreData.percentage >= 70 ? '#10B981' :
                                          scoreData.percentage >= 40 ? '#F59E0B' : '#EF4444',
                                        textColor: scoreData.percentage >= 70 ? '#10B981' :
                                          scoreData.percentage >= 40 ? '#F59E0B' : '#EF4444',
                                        trailColor: '#E5E7EB',
                                      })}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    } else {
      return (
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <div className="text-center py-8">
            <div className="inline-block p-6 rounded-full bg-yellow-100 mb-6">
              <FaLock className="text-4xl text-yellow-600" />
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              Unlock Full Mock Test Package
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-lg">
              Get complete access to <strong>10 full mock tests</strong>, each containing all 4 subjects:
              Mathematics, Thinking Skills, Writing, and Reading. Perfect for comprehensive exam preparation.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 max-w-md mx-auto">
              <h4 className="font-bold text-blue-800 text-xl mb-4">Package Includes:</h4>
              <div className="grid grid-cols-2 gap-4 text-left">
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>10 Mock Tests</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Mathematics</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Thinking Skills</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Writing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Reading</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className="text-green-500" />
                  <span>Detailed Analytics</span>
                </div>
              </div>
            </div>

            <div className="mb-8 max-w-md mx-auto">
              <CouponInput
                onCouponApplied={(coupon) => setAppliedCoupons(prev => ({ ...prev, 'mocktest-package': coupon }))}
                onCouponRemoved={() => setAppliedCoupons(prev => {
                  const newCoupons = { ...prev };
                  delete newCoupons['mocktest-package'];
                  return newCoupons;
                })}
                type="mocktest-package"
              />
            </div>

            <div className="space-y-4">
              <button
                onClick={handleBuyNow}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:opacity-90 transition-all duration-300 text-lg shadow-lg hover:shadow-xl"
              >
                Buy Now - Unlock 10 Mock Tests ($450)
              </button>
              <p className="text-gray-500 text-sm">
                One-time payment • Full access for 1 year • Money-back guarantee
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-teal-50 min-h-screen py-12 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button
             onClick={() => navigate('/new-student-dashboard')}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <FaChevronLeft className="mr-1" /> Back to Dashboard
          </button>
          <h2 className="text-3xl font-bold text-gray-800 mt-4">Mock Tests</h2>
          <p className="text-gray-600">Complete tests under exam conditions</p>
        </div>

        {/* Mock Test Package Section */}
        {renderMockTestPackage()}

        {/* ✅ EXACT SAME TEST HISTORY SECTION AS OLD CODE */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaHistory className="mr-2 text-blue-500" /> Your Mock Test History
            </h3>
            <button
              onClick={() => navigate('/test-history', { state: { filter: 'mock' } })}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View All <FaChevronRight className="inline ml-1" />
            </button>
          </div>

          {testHistory.length > 0 ? (
            <div className="space-y-4">
              {testHistory.slice(0, 3).map((test, index) => {
                const scoreData = getScoreDisplay(test);
                const scoreText = getScoreText(test);

                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/test-results/mock/${test.testId}`)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800">
                          {getTestDisplayName(test)}
                        </h4>
                        <p className="text-sm text-gray-500">{formatTestDate(test.completedAt)}</p>

                        {/* ✅ UPDATED: Show appropriate score information */}
                        <div className="text-sm text-gray-600 mt-1">
                          {test.subject === 'writing' ? (
                            <div className="space-y-1">
                              <div>
                                <span className="font-medium">
                                  Score: {scoreData.displayValue}
                                  {scoreData.hasTeacherScore && (
                                    <span className="ml-1 text-xs text-green-600">(Teacher Graded)</span>
                                  )}
                                </span>
                                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${scoreData.rawScore >= 8 ? 'bg-green-100 text-green-800' :
                                  scoreData.rawScore >= 6 ? 'bg-blue-100 text-blue-800' :
                                    scoreData.rawScore >= 4 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                  }`}>
                                  {scoreText}
                                </span>
                              </div>
                              {test.teacher_feedback && (
                                <div className="text-xs text-gray-500">
                                  Teacher feedback available
                                </div>
                              )}
                            </div>
                          ) : (
                            <p>
                              {/* {test.correctAnswers || 0} correct • {test.incorrectAnswers || 0} incorrect */}
                            </p>
                          )}
                        </div>

                        {/* ✅ ADD: Show teacher grading status for writing tests */}
                        {test.subject === 'writing' && test.status === 'pending_admin_review' && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                              Awaiting Teacher Review
                            </span>
                          </div>
                        )}
                        {test.subject === 'writing' && test.status === 'assigned' && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                              Assigned to Teacher
                            </span>
                          </div>
                        )}
                        {test.subject === 'writing' && test.status === 'graded' && scoreData.hasTeacherScore && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                              ✅ Teacher Graded
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center ml-4">
                        <div style={{ width: 50, height: 50, marginRight: 12 }}>
                          <CircularProgressbar
                            value={scoreData.percentage}
                            text={scoreData.isWriting ? `${scoreData.rawScore}` : `${Math.round(scoreData.percentage)}%`}
                            styles={buildStyles({
                              textSize: scoreData.isWriting ? '32px' : '28px',
                              pathColor: scoreData.percentage >= 70 ? '#10B981' :
                                scoreData.percentage >= 40 ? '#F59E0B' : '#EF4444',
                              textColor: scoreData.percentage >= 70 ? '#10B981' :
                                scoreData.percentage >= 40 ? '#F59E0B' : '#EF4444',
                              trailColor: '#E5E7EB',
                            })}
                          />
                        </div>
                        {test.subject === 'writing' && (
                          <div className="text-xs text-gray-500 text-center">
                            /10
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="inline-block p-4 rounded-full bg-gray-100 mb-4">
                <FaHistory className="text-3xl text-gray-400" />
              </div>
              <p className="text-gray-500">No mock test history available</p>
              <p className="text-gray-400 text-sm mt-2">Complete your first mock test to see your history here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MockTestsPage;